import { ProblemDetails } from './problemDetails.types';

const ERROR_CODE_PATTERN = /^\/error[#/](.+)$/;

/**
 * Extracts the stable error code from a Problem Details `type`, tolerating both
 * the current `/error/<code>` form and the legacy `/error#<code>` hash form.
 * @param type - The raw `type` value from the problem response.
 * @returns The error code, or `undefined` when `type` does not match.
 */
export function extractErrorCode(type: string | undefined): string | undefined {
  const match = ERROR_CODE_PATTERN.exec(type ?? '');
  if (!match) {
    return undefined;
  }
  const [, code] = match;
  return code;
}

/**
 * Parses an unknown API error into `ProblemDetails` when it is an RFC 9457
 * `application/problem+json` response with a recognizable error code. The
 * `@hey-api` clients throw the already-parsed error body (status attached by
 * the shared interceptor, see `src/api/clients/interceptors.ts`), so the error
 * is read as a record directly.
 * @param error - The error thrown by an API client call.
 * @returns The parsed problem details, or `undefined` for non-problem errors.
 */
export function parseProblemDetails(error: unknown): ProblemDetails | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const { type, title, status, detail } = error as Record<string, unknown>;
  const typeValue = typeof type === 'string' ? type : undefined;
  const code = extractErrorCode(typeValue);
  if (!code || typeof status !== 'number') {
    return undefined;
  }

  return {
    code,
    type: typeValue ?? '',
    title: typeof title === 'string' ? title : '',
    status,
    detail: typeof detail === 'string' ? detail : undefined,
  };
}
